
import React, { useState, useEffect } from 'react';
import { Subscription, Invoice, Customer, SubscriptionStatus, PlanTier } from '../types';
import { api } from '../services/api';
import InvoiceTable from './InvoiceTable';
import { CURRENCIES, formatValue } from '../constants';
import { useCurrency } from './CurrencyContext';

interface SubscriptionDetailProps {
  subscriptionId: string;
  onBack: () => void;
  onNavigateToCustomer: (customerId: string) => void;
}

const SubscriptionDetail: React.FC<SubscriptionDetailProps> = ({ subscriptionId, onBack, onNavigateToCustomer }) => {
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [selectedNewPlan, setSelectedNewPlan] = useState<PlanTier>(PlanTier.PRO);
  const { currencyCode } = useCurrency();

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const subs = await api.subscriptions.getAll();
        const sub = subs.find(s => s.id === subscriptionId);
        
        if (sub) {
          setSubscription(sub);
          const [cust, invs] = await Promise.all([
            api.customers.getOne(sub.customerId),
            api.invoices.getAll()
          ]);
          setCustomer(cust || null);
          setInvoices(invs.filter(i => i.customerId === sub.customerId));
        }
      } catch (e) {
        console.error("Error loading subscription details", e);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [subscriptionId]);

  const handleUpdatePlan = async () => {
    if (!subscription) return;
    try {
      await api.subscriptions.update(subscription.id, { plan: selectedNewPlan });
      setIsUpgrading(false);
      window.location.reload(); // Simple refresh to update state
    } catch (err) {
      alert('Plan update failed. Contact gateway support.');
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );
  }

  if (!subscription) return null;

  const totalRevenue = invoices.filter(i => i.status === 'Paid').reduce((sum, i) => sum + i.amount, 0);

  return (
    <div className="space-y-8 animate-in slide-in-from-right-8 duration-500 pb-24">
      <div className="flex flex-col gap-6">
        <button onClick={onBack} className="self-start flex items-center gap-2 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          <span className="font-bold text-sm uppercase tracking-wider">Back to Subscriptions</span>
        </button>

        <div className="bg-white dark:bg-slate-800 rounded-[2.5rem] p-8 border border-slate-200 dark:border-slate-700 shadow-sm relative overflow-hidden">
          <div className="relative z-10 flex flex-col lg:flex-row justify-between gap-8 items-start lg:items-center">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight font-mono">{subscription.id}</h1>
                <span className={`px-4 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest shadow-lg ${subscription.status === SubscriptionStatus.ACTIVE ? 'bg-emerald-500' : 'bg-slate-500'} text-white`}>
                  {subscription.status}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm font-bold text-slate-500 dark:text-slate-400">
                <span>{subscription.plan} Plan</span>
                <span className="w-1 h-1 bg-slate-300 rounded-full"></span>
                <button onClick={() => onNavigateToCustomer(subscription.customerId)} className="text-indigo-600 hover:underline">{customer?.name || subscription.customerName}</button>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="bg-slate-50 dark:bg-slate-700/50 p-6 rounded-3xl min-w-[160px]">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Monthly MRR</p>
                <p className="text-2xl font-black text-slate-900 dark:text-white">{formatValue(subscription.mrr, currencyCode)}</p>
              </div>
              <div className="bg-slate-900 dark:bg-black p-6 rounded-3xl min-w-[160px] text-white">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">LTV Impact</p>
                <p className="text-2xl font-black text-emerald-400">{formatValue(totalRevenue, currencyCode)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="space-y-8">
           <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 p-8 shadow-sm">
              <h3 className="text-lg font-black text-slate-900 dark:text-white mb-6 uppercase tracking-tight">Configuration</h3>
              <div className="space-y-6">
                 {[
                   { label: 'Start Date', value: new Date(subscription.startDate).toLocaleDateString() },
                   { label: 'Next Billing', value: subscription.nextBillingDate },
                   { label: 'Gateway ID', value: subscription.gatewayRef, mono: true }
                 ].map((item, i) => (
                   <div key={i} className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-700 last:border-0 last:pb-0">
                      <span className="text-xs font-bold text-slate-500 dark:text-slate-400">{item.label}</span>
                      <span className={`text-sm font-black text-slate-900 dark:text-white ${item.mono ? 'font-mono text-xs' : ''}`}>{item.value}</span>
                   </div>
                 ))}
              </div>
           </div>

           <div className="bg-indigo-900 text-white rounded-3xl p-8 shadow-lg">
              <h3 className="text-lg font-black mb-6">Subscription Actions</h3>
              <div className="space-y-3">
                 <button onClick={() => setIsUpgrading(true)} className="w-full py-4 bg-white text-indigo-900 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-50 transition-all">Upgrade / Downgrade</button>
                 <button className="w-full py-4 bg-indigo-800 text-white border border-indigo-700 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 transition-all">Pause Billing</button>
                 <button className="w-full py-4 bg-transparent text-red-300 border border-red-900/30 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-900/20 transition-all">Cancel Subscription</button>
              </div>
           </div>
        </div>

        <div className="lg:col-span-2 space-y-8">
           <div className="bg-white dark:bg-slate-800 rounded-3xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className="p-8 border-b border-slate-100 dark:border-slate-700 flex justify-between items-center">
                 <h3 className="text-lg font-black text-slate-900 dark:text-white">Transaction Log</h3>
                 <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{invoices.length} Invoices Found</span>
              </div>
              <InvoiceTable invoices={invoices} />
           </div>
        </div>
      </div>

      {/* Plan Transition Modal */}
      {isUpgrading && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
           <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" onClick={() => setIsUpgrading(false)}></div>
           <div className="relative bg-white dark:bg-slate-800 rounded-[3rem] shadow-2xl w-full max-w-xl overflow-hidden animate-in zoom-in-95">
              <div className="p-10 border-b border-slate-100 dark:border-slate-700">
                 <h2 className="text-2xl font-black text-slate-900 dark:text-white">Plan Orchestrator</h2>
                 <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Transitioning from <span className="font-bold text-indigo-600 uppercase">{subscription.plan}</span></p>
              </div>
              
              <div className="p-10 space-y-8">
                 <div className="grid grid-cols-2 gap-4">
                    {[PlanTier.STARTER, PlanTier.PRO, PlanTier.ENTERPRISE].map(tier => (
                      <button 
                        key={tier}
                        onClick={() => setSelectedNewPlan(tier)}
                        className={`p-6 rounded-3xl border-2 transition-all text-left ${selectedNewPlan === tier ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                      >
                         <p className={`text-[10px] font-black uppercase tracking-widest ${selectedNewPlan === tier ? 'text-indigo-600' : 'text-slate-400'}`}>{tier}</p>
                         <p className="text-lg font-black text-slate-900 dark:text-white mt-1">${tier === 'Starter' ? '39' : tier === 'Pro' ? '159' : '999'}</p>
                      </button>
                    ))}
                 </div>

                 {/* Pro-rata Simulation */}
                 <div className="bg-slate-900 dark:bg-black p-8 rounded-[2rem] text-white">
                    <h4 className="text-[10px] font-black text-indigo-400 uppercase tracking-widest mb-6">Financial Impact (Pro-rated)</h4>
                    <div className="space-y-4">
                       <div className="flex justify-between text-xs text-slate-400 font-bold">
                          <span>Unused credit ({subscription.plan})</span>
                          <span className="text-emerald-400">-$14.20</span>
                       </div>
                       <div className="flex justify-between text-xs text-slate-400 font-bold">
                          <span>Remaining period ({selectedNewPlan})</span>
                          <span>+$42.50</span>
                       </div>
                       <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                          <span className="text-sm font-black uppercase">Due Today</span>
                          <span className="text-2xl font-black text-white">$28.30</span>
                       </div>
                    </div>
                 </div>

                 <button 
                  onClick={handleUpdatePlan}
                  className="w-full py-5 bg-indigo-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-indigo-700 shadow-xl shadow-indigo-100 dark:shadow-none transition-all active:scale-95"
                 >
                   Confirm Subscription Change
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionDetail;
